import { makeNamespacedLog } from '@sb/log';

// We import the *store* (`userIDStore`) — not the higher-level
// `currentUserID()` — so this module stays cycle-free.
// `currentUserID()` wires reAuthenticate + error hooks via `getApp`,
// which transitively imports `sessionLifecycle`, which imports this
// file. Going through `userIDStore` (no `getApp` dependency) avoids
// that 5-hop module cycle.
import { userIDStore } from './utils/userIDStore';

const log = makeNamespacedLog('session.invalidate');

/**
 * Subset of the feathers app surface this module relies on. Typed
 * structurally so callers can pass either the real feathers app or a
 * minimal stub in tests, and so this module stays free of imports from
 * `../utils/getApp` (which would create an `import/no-cycle` between
 * `getApp.ts → sessionLifecycle.ts → invalidateSession.ts → getApp.ts`).
 */
type LogoutAppLike = {
  logout(): Promise<unknown>;
};

/**
 * Single funnel for ending the operator's authenticated session.
 *
 * Three side effects, in order of importance:
 * 1. **Load-bearing**: clear `localStorage.feathers-jwt`. This is what
 *    makes subsequent re-auth attempts fail closed.
 * 2. **Load-bearing**: set `userIDStore()` to `null`. Nothing observes
 *    the feathers `'logout'` event for the userID — the only existing
 *    paths that clear userID are (a) initial `reAuthenticate()` failure
 *    at boot and (b) the `getApp().hooks({ error })` handler firing on a
 *    server-side `NotAuthenticated`. Without this explicit set, the
 *    timer-driven kick would clear the JWT but leave userID populated,
 *    so `PrivatePage` would not navigate to `/login` until the operator
 *    happened to make a server call that returned 401.
 * 3. **Best-effort**: call `app.logout()`. This fires the
 *    feathers-client `'logout'` event that clears the session-lifecycle
 *    timer and notifies any other subscribers. If `logout()` rejects
 *    (e.g. the socket is already gone), we log a warning and continue —
 *    the JWT has already been cleared and userID has already been
 *    nulled, so the auth state is effectively dead either way.
 *
 * `invalidateSession()` does *not* navigate directly. It relies on
 * `PrivatePage` observing `userID === null` and issuing the
 * `/login?redirectTo=…` navigation. This keeps a single source of truth
 * for the redirect URL and avoids two systems racing to navigate.
 */
export function invalidateSession(app: LogoutAppLike): void {
  localStorage.removeItem('feathers-jwt');
  userIDStore().set(null);

  app.logout().catch((err) => {
    log.warn('logoutFailed', 'app.logout() rejected; JWT already cleared', {
      err,
    });
  });
}
